import {
  getLocalStorageValue,
  getSessionStorageValue,
  getUserTypeFromPaidBatch,
  isEmpty,
} from '@/utils/common';
import { loadMoengageScript } from '@/utils/moengage';
import { logEvent as gaLogEvent, getAnalytics, isSupported } from 'firebase/analytics';
import { getApps, initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: 'AIzaSyBhYL7O5mJbYRpchmYstZuNNm9EkoXsISs',
  authDomain: 'curiousjr-app.firebaseapp.com',
  databaseURL: 'https://curiousjr-app.firebaseio.com',
  projectId: 'curiousjr-app',
  storageBucket: 'curiousjr-app.appspot.com',
  messagingSenderId: '979752599489',
  appId: '1:979752599489:web:88435d173d4a8e81d6f940',
  measurementId: 'G-YLS8T7FSRC',
};

// Initialize Firebase app once
let firebaseApp: any = null;

const getFirebaseApp = () => {
  if (!firebaseApp) {
    // Check if Firebase app already exists
    const existingApps = getApps();
    if (existingApps.length > 0) {
      firebaseApp = existingApps[0];
    } else {
      firebaseApp = initializeApp(firebaseConfig);
    }
  }
  return firebaseApp;
};

export const getUserParams = (forMoengage: boolean = false) => {
  let userData: any = getLocalStorageValue('user');
  if (userData) {
    userData = JSON.parse(userData);
  }
  const { paid_batch_type } = getPaidParams({
    is_paid_batch_type_required: true,
    is_user_status_required: true,
  });

  if (isEmpty(userData)) {
    return {
      user_id: '',
      user_class: '',
      user_name: '',
      pw_user_id: '',
      user_cohort: '',
    };
  }

  const profile_id = userData?.profileId?._id;
  const user_id = userData?.id;
  let user_name = userData?.firstName;

  if (userData?.lastName) {
    user_name += ' ' + userData?.lastName;
  }

  const user_class = userData?.profileId?.class;
  const user_cohort = userData?.profileId?.cohortId;
  const selectedCohort = getSessionStorageValue('selectedCohortId');

  const selectedCohortObj = selectedCohort != null ? { selected_cohort: selectedCohort } : {};

  if (forMoengage) {
    return {
      user_id,
      user_class,
      user_name,
      user_cohort,
      ...selectedCohortObj,
      user_country: userData?.countryGroup,
      country_code: userData?.countryCode,
      user_created_at: userData?.createdAt,
      mobile_number: userData?.primaryNumber,
      user_type: getUserTypeFromPaidBatch(paid_batch_type) || '',
      user_grade: userData?.profileId?.class || '',
      language_preferred: userData?.profileId?.language || '',
      flow: 'revamped',
      URL: typeof window !== 'undefined' ? window.location.href : '',
      moe_logged_in_status: !!user_id,
    };
  } else {
    return {
      user_id: profile_id,
      pw_user_id: user_id,
      user_cohort,
      user_class,
      user_name,
      user_created_at: userData?.createdAt,
      user_country: userData?.countryGroup,
      country_code: userData?.countryCode,
      ...selectedCohortObj,
      flow: 'revamped',
    };
  }
};

export const getCampaignParams = (forMoengage: boolean = false) => {
  if (typeof window === 'undefined') {
    return {};
  }
  const searchParams = new URLSearchParams(window.location.search);
  const utmParamKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'igshid',
  ];

  let areUtmParamsPresentInUrl = false;

  const utmParams: {
    [value: string]: string;
  } = {};

  searchParams.forEach((value, key) => {
    if (utmParamKeys.includes(key)) {
      areUtmParamsPresentInUrl = true;
      utmParams[key] = value;
    }
  });

  // utm_source=streaks overrides only the source; other UTMs stay from the stored campaign.
  const isFromStreaks = utmParams.utm_source === 'streaks';

  if (areUtmParamsPresentInUrl && !isFromStreaks) {
    if (forMoengage) {
      return {
        utm_source: utmParams.utm_source || '',
        utm_medium: utmParams.utm_medium || '',
        utm_campaign: utmParams.utm_campaign || '',
      };
    } else {
      return {
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        gclid: utmParams.gclid,
        fbclid: utmParams.fbclid,
        igshid: utmParams.igshid,
      };
    }
  } else {
    const storedUtmParams = localStorage.getItem('utmParams');
    const stored = JSON.parse(storedUtmParams || '{}');
    const { utm_medium, utm_campaign, utm_term, utm_content, gclid, fbclid, igshid } = stored;
    const utm_source = isFromStreaks ? 'streaks' : stored.utm_source;

    if (forMoengage) {
      return {
        utm_source: utm_source || '',
        utm_medium: utm_medium || '',
        utm_campaign: utm_campaign || '',
      };
    } else {
      return {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        gclid,
        fbclid,
        igshid,
      };
    }
  }
};

export const getSource = () => {
  if (typeof window === 'undefined') {
    return 'web';
  }
  const fromApp = localStorage.getItem('from_app');

  if (!fromApp || fromApp === 'false') {
    return 'web';
  }

  return 'webapp';
};

export const getPaidParams = ({
  is_paid_batch_type_required,
  is_user_status_required,
}: {
  is_paid_batch_type_required: boolean;
  is_user_status_required: boolean;
}) => {
  if (typeof window === 'undefined') {
    return {};
  }

  const paidDataString = localStorage.getItem('paidData');

  const { user_status, paid_batch_type } = JSON.parse(paidDataString || '{}');

  if (is_user_status_required && is_paid_batch_type_required) {
    return {
      user_status: user_status || '',
      paid_batch_type: paid_batch_type || '',
    };
  }

  if (is_user_status_required) {
    return {
      user_status: user_status || '',
    };
  }

  if (is_paid_batch_type_required) {
    return {
      paid_batch_type: paid_batch_type || '',
    };
  }

  return {};
};

export const trackGaEvent = ({
  eventName,
  eventParams,
}: {
  eventName: string;
  eventParams?: any;
}) => {
  const userData = getUserParams();

  const utmParams = getCampaignParams();

  // Use source from eventParams if provided, otherwise use global default
  const source = eventParams?.source || getSource();

  let paidParams = {};
  if (eventParams?.is_paid_batch_type_required || eventParams?.is_user_status_required) {
    paidParams = getPaidParams({
      is_user_status_required: eventParams?.is_user_status_required,
      is_paid_batch_type_required: eventParams?.is_paid_batch_type_required,
    });
    delete eventParams.is_paid_batch_type_required;
    delete eventParams.is_user_status_required;
  }

  const updatedData = {
    ...userData,
    ...utmParams,
    ...paidParams,
    ...eventParams,
    source,
  };

  // if (process.env.NEXT_PUBLIC_ENV === 'production') {
  if (true) {
    // Use the singleton Firebase app instance
    const app = getFirebaseApp();
    isSupported().then(isBrowser => (isBrowser ? invokeAnalytics() : null));

    const invokeAnalytics = () => {
      const analytics = getAnalytics(app);
      gaLogEvent(analytics, eventName, updatedData);
    };
  } else {
    console.log('GA_EVENT', {
      name: eventName,
      eventParams: updatedData,
    });
  }
};

export const moengageLogin = ({ user }: { user: any }) => {
  try {
    const moengage = (window as any)?.Moengage;
    if (!moengage) {
      console.warn('MoEngage not available for user login');
      return;
    }

    // Safe user attribute setting with error handling
    if (user?.id && typeof moengage.add_unique_user_id === 'function') {
      moengage.add_unique_user_id(user.id);
    }
    if (user?.firstName && typeof moengage.add_first_name === 'function') {
      moengage.add_first_name(user.firstName);
    }
    if (user?.lastName && typeof moengage.add_last_name === 'function') {
      moengage.add_last_name(user.lastName);
    }
    if (user?.primaryNumber && typeof moengage.add_mobile === 'function') {
      moengage.add_mobile(user.primaryNumber);
    }
    if (user?.username && typeof moengage.add_user_name === 'function') {
      moengage.add_user_name(user.username);
    }
  } catch (e) {
    console.warn('MoEngage login error (handled):', e);
  }
};

export const moengageLogout = () => {
  (window as any)?.Moengage?.destroy_session?.();
};

export const triggerMoengageLoginIfUserIsPresent = () => {
  const user = getLocalStorageValue('user', {});
  if (!isEmpty(user)) {
    moengageLogin({ user });
  }
};

export const trackMoengageEvent = async ({
  eventName,
  eventParams,
}: {
  eventName: string;
  eventParams?: any;
}) => {
  if (typeof window !== 'undefined' && isEmpty((window as any)?.Moengage)) {
    console.log('LOADING MOENGAGE SCRIPT');
    try {
      await loadMoengageScript();
      // Wait for initialization
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      console.warn('Failed to load MoEngage script:', error);
    }
  }
  const userData = getUserParams(true);

  if (!userData?.user_id) return;

  if (eventParams?.delete_selected_cohort) {
    if ('selected_cohort' in userData) {
      delete userData.selected_cohort;
    }
    delete eventParams.delete_selected_cohort;
  }

  const utmParams = getCampaignParams(true);

  // Use source from eventParams if provided, otherwise use global default
  const source = eventParams?.source || getSource();

  let paidParams = {};
  if (eventParams?.is_paid_batch_type_required || eventParams?.is_user_status_required) {
    paidParams = getPaidParams({
      is_user_status_required: eventParams?.is_user_status_required,
      is_paid_batch_type_required: eventParams?.is_paid_batch_type_required,
    });
    delete eventParams.is_paid_batch_type_required;
    delete eventParams.is_user_status_required;
  }

  if (eventParams?.delete_selected_cohort) {
    if ('selected_cohort' in userData) {
      delete userData.selected_cohort;
    }
    delete eventParams.delete_selected_cohort;
  }

  const updatedData = {
    ...userData,
    ...utmParams,
    ...paidParams,
    ...eventParams,
    source,
  };
  console.log('MOE EVENT >>>>>>', updatedData);

  // Enhanced error handling for MoEngage tracking
  try {
    const moengage = (window as any)?.Moengage;

    // Check if MoEngage is properly loaded and initialized
    if (moengage && typeof moengage.track_event === 'function') {
      // Ensure user is properly set up before tracking events
      if (updatedData.user_id) {
        // Set user attributes safely
        try {
          if (typeof moengage.add_unique_user_id === 'function') {
            moengage.add_unique_user_id(updatedData.user_id);
          }
          if (updatedData.user_name && typeof moengage.add_user_name === 'function') {
            moengage.add_user_name(updatedData.user_name);
          }
          if (updatedData.mobile_number && typeof moengage.add_mobile === 'function') {
            moengage.add_mobile(updatedData.mobile_number);
          }
        } catch (attrError) {
          // Suppress user attribute errors but continue with event tracking
          console.warn('MoEngage user attribute warning (continuing):', attrError);
        }
      }

      // Track the event
      await moengage.track_event(eventName, updatedData);
      console.log('✅ MoEngage event tracked:', eventName, updatedData);
    } else {
      // Wait a bit for MoEngage to initialize and try again
      setTimeout(() => {
        const moengageRetry = (window as any)?.Moengage;
        if (moengageRetry && typeof moengageRetry.track_event === 'function') {
          moengageRetry.track_event(eventName, updatedData);
          console.log('✅ MoEngage event tracked (retry):', eventName);
        } else {
          console.warn('MoEngage not available, event skipped:', eventName);
        }
      }, 1000);
    }
  } catch (e) {
    console.warn('MoEngage tracking error (handled):', e);
  }
};
